<template>
  <div class="travel-page">
    <header class="page-header">
      <h1 class="page-title">景點探索</h1>
      <div class="header-divider"></div>
      <p class="page-subtitle">走進烏來山林溫泉，感受泰雅文化的慢旅行</p>
    </header>

    <section class="section">
      <div class="section-header">
        <div class="header-left">
          <span class="section-number">01</span>
          <h2 class="section-title">周邊景點</h2>
        </div>
      </div>
      <div class="scroll-wrapper">
        <button
          class="nav-button nav-button-left"
          @click="scrollLeft('attractions')"
          :class="{ 'nav-button-hidden': !showLeftButton.attractions }"
          aria-label="向左滑動"
        >
          <svg class="nav-icon" viewBox="0 0 24 24" fill="none">
            <path
              d="M15 18l-6-6 6-6"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <div
          class="masonry-scroll"
          ref="attractionsScroll"
          @scroll="handleScroll('attractions')"
        >
          <article
            v-for="(attraction, index) in attractions"
            :key="attraction.id"
            :class="['masonry-item', `item-${index % 3}`]"
            @click="handleCardClick('attraction', attraction)"
          >
            <div class="item-image">
              <img :src="attraction.image" :alt="attraction.name" />
            </div>
            <div class="item-content">
              <h3 class="item-title">{{ attraction.name }}</h3>
              <p class="item-description">{{ attraction.description }}</p>
            </div>
          </article>
        </div>
        <button
          class="nav-button nav-button-right"
          @click="scrollRight('attractions')"
          :class="{ 'nav-button-hidden': !showRightButton.attractions }"
          aria-label="向右滑動"
        >
          <svg class="nav-icon" viewBox="0 0 24 24" fill="none">
            <path
              d="M9 6l6 6-6 6"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
    </section>

    <section class="section">
      <div class="section-header">
        <div class="header-left">
          <span class="section-number">02</span>
          <h2 class="section-title">在地美食</h2>
        </div>
      </div>
      <div class="scroll-wrapper">
        <button
          class="nav-button nav-button-left"
          @click="scrollLeft('foods')"
          :class="{ 'nav-button-hidden': !showLeftButton.foods }"
          aria-label="向左滑動"
        >
          <svg class="nav-icon" viewBox="0 0 24 24" fill="none">
            <path
              d="M15 18l-6-6 6-6"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <div
          class="masonry-scroll"
          ref="foodsScroll"
          @scroll="handleScroll('foods')"
        >
          <article
            v-for="(food, index) in foods"
            :key="food.id"
            :class="['masonry-item', `item-${index % 3}`]"
            @click="handleCardClick('food', food)"
          >
            <div class="item-image">
              <img :src="food.image" :alt="food.name" />
              <div class="image-overlay">
                <span class="overlay-badge badge-food">{{
                  food.category
                }}</span>
              </div>
            </div>
            <div class="item-content">
              <h3 class="item-title">{{ food.name }}</h3>
              <p class="item-description">{{ food.description }}</p>
              <div class="item-footer">
                <span class="footer-rating">{{ food.popularity }}</span>
              </div>
            </div>
          </article>
        </div>
        <button
          class="nav-button nav-button-right"
          @click="scrollRight('foods')"
          :class="{ 'nav-button-hidden': !showRightButton.foods }"
          aria-label="向右滑動"
        >
          <svg class="nav-icon" viewBox="0 0 24 24" fill="none">
            <path
              d="M9 6l6 6-6 6"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
    </section>

    <section class="section">
      <div class="section-header">
        <div class="header-left">
          <span class="section-number">03</span>
          <h2 class="section-title">伴手禮推薦</h2>
        </div>
      </div>
      <div class="scroll-wrapper">
        <button
          class="nav-button nav-button-left"
          @click="scrollLeft('souvenirs')"
          :class="{ 'nav-button-hidden': !showLeftButton.souvenirs }"
          aria-label="向左滑動"
        >
          <svg class="nav-icon" viewBox="0 0 24 24" fill="none">
            <path
              d="M15 18l-6-6 6-6"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <div
          class="masonry-scroll"
          ref="souvenirsScroll"
          @scroll="handleScroll('souvenirs')"
        >
          <article
            v-for="(souvenir, index) in souvenirs"
            :key="souvenir.id"
            :class="['masonry-item', `item-${index % 3}`]"
            @click="handleCardClick('souvenir', souvenir)"
          >
            <div class="item-image">
              <img :src="souvenir.image" :alt="souvenir.name" />
              <div class="image-overlay">
                <span class="overlay-badge badge-souvenir">推薦</span>
              </div>
            </div>
            <div class="item-content">
              <h3 class="item-title">{{ souvenir.name }}</h3>
              <p class="item-description">{{ souvenir.description }}</p>
            </div>
          </article>
        </div>
        <button
          class="nav-button nav-button-right"
          @click="scrollRight('souvenirs')"
          :class="{ 'nav-button-hidden': !showRightButton.souvenirs }"
          aria-label="向右滑動"
        >
          <svg class="nav-icon" viewBox="0 0 24 24" fill="none">
            <path
              d="M9 6l6 6-6 6"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: "TravelPage",
  data() {
    return {
      attractions: [
        {
          id: 1,
          name: "烏來老街",
          description:
            "結合泰雅文化與在地小吃的經典老街，感受烏來最原始的人文風情",
          image: require("@/assets/picture/Travel/wulai-1.webp"), // 控制區域：載入本地圖片
        },
        {
          id: 2,
          name: "烏來瀑布",
          description: "北台灣著名瀑布景點，氣勢磅礡，四季皆有不同風貌",
          image: require("@/assets/picture/Travel/wulai-2.webp"),
        },
        {
          id: 3,
          name: "烏來紅河谷",
          description: "沿溪而行的平緩步道，適合散步與欣賞山林景色",
          image: require("@/assets/picture/Travel/wulai-5.webp"),
        },
        {
          id: 4,
          name: "雲仙樂園",
          description: "搭乘空中纜車進入森林樂園，適合親子與輕鬆健行",
          image: require("@/assets/picture/Travel/wulai-3.webp"),
        },
        {
          id: 5,
          name: "內洞森林遊樂區",
          description: "擁有多層瀑布與豐富芬多精的療癒系森林步道",
          image: require("@/assets/picture/Travel/wulai-4.webp"),
        },
      ],
      foods: [
        {
          id: 6,
          name: "石板烤山豬肉",
          description:
            "原住民傳統料理，將醃製過的山豬肉放在石板上炙烤，外酥內嫩並帶有煙燻香氣",
          image: require("@/assets/picture/Travel/wulai-6.webp"),
          category: "原住民料理",
          popularity: "必吃",
        }, // 控制「原住民經典主食」顯示區塊

        {
          id: 7,
          name: "馬告香腸",
          description:
            "加入原住民香料馬告製作的香腸，帶有檸檬與胡椒的清香風味，是烏來代表性小吃",
          image: require("@/assets/picture/Travel/wulai-7.webp"),
          category: "在地小吃",
          popularity: "人氣",
        }, // 控制「街邊特色小吃」顯示區塊

        {
          id: 8,
          name: "竹筒飯",
          description:
            "將糯米與配料放入竹筒中蒸熟，吸收竹香，口感Q彈，是原住民常見主食",
          image: require("@/assets/picture/Travel/wulai-8.webp"),
          category: "原住民料理",
          popularity: "推薦",
        }, // 控制「傳統主食介紹」區塊

        {
          id: 9,
          name: "溪魚炭烤",
          description:
            "使用南勢溪新鮮溪魚，以炭火簡單燒烤並灑上鹽巴與馬告，風味自然純粹",
          image: require("@/assets/picture/Travel/wulai-9.webp"),
          category: "在地料理",
          popularity: "推薦",
        }, // 控制「自然系料理」區塊

        {
          id: 10,
          name: "溫泉蛋",
          description:
            "利用烏來天然溫泉水低溫慢煮而成，蛋白滑嫩、蛋黃半熟，是簡單卻經典的小吃",
          image: require("@/assets/picture/Travel/wulai-10.webp"),
          category: "特色小吃",
          popularity: "必吃",
        }, // 控制「溫泉相關美食」區塊
      ],

      souvenirs: [
        {
          id: 6,
          name: "馬告辣椒醬",
          description:
            "以原住民香料馬告製成的辣椒醬，帶有檸檬與胡椒香氣，適合拌飯或沾肉",
          image: require("@/assets/picture/Travel/wulai-11.webp"),
        }, // 控制「在地風味調味品」顯示區塊

        {
          id: 7,
          name: "小米麻糬",
          description:
            "以小米與糯米製作的傳統麻糬，口感Q彈不黏牙，帶有淡淡穀物香氣，是原住民常見點心",
          image: require("@/assets/picture/Travel/wulai-12.webp"),
        }, // 控制「傳統甜點伴手禮」顯示區塊

        {
          id: 8,
          name: "溫泉皮蛋",
          description:
            "利用烏來天然溫泉熱度製作的皮蛋，蛋黃綿密、風味濃厚，是少見且具地方特色的伴手禮",
          image: require("@/assets/picture/Travel/wulai-13.webp"),
        }, // 控制「溫泉特色伴手禮」顯示區塊

        {
          id: 9,
          name: "泰雅編織品",
          description:
            "由泰雅族工藝師手工編織的布料與飾品，圖騰獨特且富有文化意義，是極具收藏價值的伴手禮",
          image: require("@/assets/picture/Travel/wulai-14.webp"),
        }, // 控制「原住民工藝品」顯示區塊

        {
          id: 10,
          name: "蔬果乾貨包",
          description:
            "將在地蔬果風乾製成的乾貨包，方便攜帶且富含營養，是健康又實用的伴手禮選擇",
          image: require("@/assets/picture/Travel/wulai-15.webp"),
        }, // 控制「健康蔬果伴手禮」顯示區塊
      ],

      showLeftButton: {
        attractions: false,
        foods: false,
        souvenirs: false,
      },
      showRightButton: {
        attractions: true,
        foods: true,
        souvenirs: true,
      },
    };
  },
  mounted() {
    this.updateButtonVisibility();
    window.addEventListener("resize", this.updateButtonVisibility);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateButtonVisibility);
  },
  methods: {
    handleCardClick(type, item) {
      console.log(`點擊了${type}:`, item.name);
    },
    scrollLeft(section) {
      const refName = section + "Scroll";
      const container = this.$refs[refName];
      if (container) {
        const scrollAmount = container.clientWidth * 0.8;
        container.scrollBy({
          left: -scrollAmount,
          behavior: "smooth",
        });
      }
    },
    scrollRight(section) {
      const refName = section + "Scroll";
      const container = this.$refs[refName];
      if (container) {
        const scrollAmount = container.clientWidth * 0.8;
        container.scrollBy({
          left: scrollAmount,
          behavior: "smooth",
        });
      }
    },
    handleScroll(section) {
      const refName = section + "Scroll";
      const container = this.$refs[refName];
      if (container) {
        const scrollLeft = container.scrollLeft;
        const maxScroll = container.scrollWidth - container.clientWidth;

        this.showLeftButton[section] = scrollLeft > 10;
        this.showRightButton[section] = scrollLeft < maxScroll - 10;
      }
    },
    updateButtonVisibility() {
      this.$nextTick(() => {
        ["attractions", "foods", "souvenirs"].forEach((section) => {
          this.handleScroll(section);
        });
      });
    },
  },
};
</script>

<style scoped>
.travel-page {
  min-height: 100vh;
  background: white;
  padding: clamp(1.5rem, 4vw, 3rem) 0;
}

.page-header {
  text-align: center;
  padding: clamp(3rem, 6vw, 5rem) clamp(1.5rem, 4vw, 3rem)
    clamp(2rem, 4vw, 3rem);
    margin-top: 1.5rem;
}

.page-title {
  font-size: clamp(2.5rem, 6vw, 4rem);
  color: rgb(135, 129, 120);
  font-weight: 300;
  letter-spacing: 0.15em;
  margin-bottom: clamp(1rem, 2vw, 1.5rem);
}

.header-divider {
  width: clamp(60px, 10vw, 100px);
  height: 2px;
  background: rgb(135, 129, 120);
  margin: 0 auto clamp(1rem, 2vw, 1.5rem);
}

.page-subtitle {
  font-size: clamp(1rem, 2.5vw, 1.3rem);
  color: rgba(135, 129, 120, 0.6);
  font-weight: 300;
}

.section {
  margin-bottom: clamp(4rem, 8vw, 6rem);
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: clamp(2rem, 4vw, 3rem);
  padding: 0 clamp(1.5rem, 4vw, 5rem);
}

.header-left {
  display: flex;
  align-items: center;
  gap: clamp(1rem, 2vw, 1.5rem);
}

.section-number {
  font-size: clamp(1.5rem, 3vw, 2rem);
  color: rgba(135, 129, 120, 0.3);
  font-weight: 300;
}

.section-title {
  font-size: clamp(1.8rem, 4vw, 2.5rem);
  color: rgb(135, 129, 120);
  font-weight: 400;
  letter-spacing: 0.08em;
}

.scroll-wrapper {
  position: relative;
  padding: 0 clamp(1.5rem, 4vw, 4rem);
}

.masonry-scroll {
  display: flex;
  gap: clamp(1.5rem, 3vw, 2.5rem);
  overflow-x: auto;
  overflow-y: hidden;
  padding: clamp(0.5rem, 1vw, 1rem) clamp(1.5rem, 4vw, 5rem)
    clamp(1.5rem, 3vw, 2rem);
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  scroll-behavior: smooth;
}

.masonry-scroll::-webkit-scrollbar {
  display: none;
}

.nav-button {
  display: none;
}

.nav-icon {
  width: clamp(1.5rem, 2.5vw, 2rem);
  height: clamp(1.5rem, 2.5vw, 2rem);
  display: block;
  flex-shrink: 0;
}

@media (min-width: 1024px) {
  .nav-button {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 40px;
    height: 40px;
    background: white;
    border: 2px solid rgba(135, 129, 120, 0.3);
    border-radius: 50%;
    color: rgb(135, 129, 120);
    cursor: pointer;
    z-index: 10;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 2px 8px rgba(135, 129, 120, 0.08);
    padding: 0;
  }

  .nav-button:hover {
    background: rgb(135, 129, 120);
    color: white;
    border-color: rgb(135, 129, 120);
    transform: translateY(-50%) scale(1.08);
    box-shadow: 0 4px 16px rgba(135, 129, 120, 0.2);
  }

  .nav-button:active {
    transform: translateY(-50%) scale(0.98);
    box-shadow: 0 2px 8px rgba(135, 129, 120, 0.15);
  }

  .nav-button-left {
    left: clamp(1.5rem, 3vw, 2.5rem);
  }

  .nav-button-right {
    right: clamp(1.5rem, 3vw, 2.5rem);
  }

  .nav-button-hidden {
    opacity: 0;
    pointer-events: none;
    transform: translateY(-50%) scale(0.9);
  }
}

.masonry-item {
  flex: 0 0 clamp(280px, 80vw, 350px);
  background: white;
  border: 1px solid rgba(135, 129, 120, 0.15);
  overflow: hidden;
  transition: all 0.4s ease;
  cursor: pointer;
  scroll-snap-align: start;
}

.masonry-item.item-0 {
  border-radius: clamp(0.5rem, 2vw, 1rem) clamp(3rem, 6vw, 5rem)
    clamp(0.5rem, 2vw, 1rem) clamp(3rem, 6vw, 5rem);
}

.masonry-item.item-1 {
  border-radius: clamp(3rem, 6vw, 5rem) clamp(0.5rem, 2vw, 1rem)
    clamp(3rem, 6vw, 5rem) clamp(0.5rem, 2vw, 1rem);
}

.masonry-item.item-2 {
  border-radius: clamp(0.5rem, 2vw, 1rem) clamp(0.5rem, 2vw, 1rem)
    clamp(3rem, 6vw, 5rem) clamp(3rem, 6vw, 5rem);
}

.masonry-item:hover {
  transform: translateY(-8px);
  box-shadow: 0 clamp(8px, 2vw, 16px) clamp(32px, 6vw, 48px)
    rgba(135, 129, 120, 0.15);
  border-color: rgb(135, 129, 120);
}

.item-image {
  position: relative;
  padding-top: 75%;
  overflow: hidden;
}

.item-image img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}

.masonry-item:hover .item-image img {
  transform: scale(1.1);
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0) 50%,
    rgba(135, 129, 120, 0.3) 100%
  );
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  padding: clamp(1rem, 2vw, 1.5rem);
}

.overlay-badge {
  background: rgba(255, 255, 255, 0.95);
  color: rgb(135, 129, 120);
  padding: clamp(0.4rem, 1vw, 0.5rem) clamp(1rem, 2vw, 1.2rem);
  border-radius: clamp(1.5rem, 3vw, 2rem);
  font-size: clamp(0.8rem, 1.8vw, 0.9rem);
  font-weight: 500;
  backdrop-filter: blur(10px);
}

.badge-food {
  background: rgba(135, 129, 120, 0.95);
  color: white;
}

.badge-souvenir {
  background: rgba(135, 129, 120, 0.9);
  color: white;
}

.item-content {
  padding: clamp(1.5rem, 3vw, 2rem);
}

.item-title {
  font-size: clamp(1.2rem, 2.5vw, 1.5rem);
  color: rgb(135, 129, 120);
  font-weight: 500;
  margin-bottom: clamp(0.75rem, 1.5vw, 1rem);
  line-height: 1.4;
}

.item-description {
  font-size: clamp(0.9rem, 2vw, 1rem);
  color: rgba(135, 129, 120, 0.7);
  line-height: 1.7;
  margin-bottom: clamp(1rem, 2vw, 1.5rem);
}

.item-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: clamp(0.75rem, 1.5vw, 1rem);
  border-top: 1px solid rgba(135, 129, 120, 0.1);
}

.footer-tag {
  font-size: clamp(0.85rem, 1.8vw, 0.95rem);
  color: rgba(135, 129, 120, 0.7);
}

.footer-rating {
  font-size: clamp(0.85rem, 1.8vw, 0.95rem);
  color: rgb(135, 129, 120);
  font-weight: 500;
}

@media (min-width: 768px) {
  .masonry-item {
    flex: 0 0 clamp(320px, 45vw, 400px);
  }
}

@media (min-width: 1024px) {
  .masonry-item {
    flex: 0 0 clamp(350px, 32vw, 420px);
  }

  .page-header {
  text-align: center;
  padding: clamp(3rem, 6vw, 5rem) clamp(1.5rem, 4vw, 3rem)
    clamp(2rem, 4vw, 3rem);
    margin-top: 0;
}
}

@media (min-width: 1440px) {
  .masonry-item {
    flex: 0 0 clamp(380px, 28vw, 450px);
  }
}
</style>
